import { useState, useEffect, useRef } from 'react';
import { makeStyles, mergeClasses, tokens, Text, Button, Tooltip } from '@coreai/fluentui-react';
import {
  Dismiss16Regular,
  ChevronDown16Regular,
  ChevronUp16Regular,
  CheckmarkCircle24Filled,
} from '@fluentui/react-icons';
import {
  useOnboardingProgress,
  ONBOARDING_STEPS,
  getStepStatus,
  getProgressPercent,
} from '@/hooks/useOnboardingProgress';
import { useTelemetry } from '@/hooks/useTelemetry';
import { OnboardingStep } from './OnboardingStep';

const STAGGER_DELAY_MS = 60;
const BASE_DELAY_MS = 150;

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
    padding: tokens.spacingVerticalL,
    backgroundColor: tokens.colorNeutralBackground1,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusXLarge,
    maxWidth: '720px',
    width: '100%',
    marginLeft: 'auto',
    marginRight: 'auto',
    WebkitFontSmoothing: 'antialiased',
    MozOsxFontSmoothing: 'grayscale',
    // Entry animation
    opacity: 0,
    transform: 'translateY(8px)',
    animationName: {
      to: { opacity: 1, transform: 'translateY(0)' },
    },
    animationDuration: tokens.durationSlow,
    animationTimingFunction: tokens.curveDecelerateMax,
    animationFillMode: 'forwards',
    '@media (prefers-reduced-motion: reduce)': {
      animationName: 'none',
      animationDuration: '0.01ms',
      opacity: 1,
      transform: 'none',
    },
  },

  // Header row
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: tokens.spacingHorizontalM,
  },
  headerLeft: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXXS,
  },
  heading: {
    fontSize: tokens.fontSizeBase400,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
    margin: 0,
    textWrap: 'balance',
  },
  subtitle: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
  },
  headerButton: {
    flexShrink: 0,
    minWidth: '40px',
    minHeight: '40px',
    color: tokens.colorNeutralForeground3,
    ':hover': {
      color: tokens.colorNeutralForeground1,
    },
  },
  headerActions: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXXS,
  },

  // Progress bar
  progressRow: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalM,
  },
  progressTrack: {
    flex: 1,
    height: '4px',
    backgroundColor: tokens.colorNeutralBackground4,
    borderRadius: tokens.borderRadiusSmall,
    overflow: 'hidden',
  },
  progressFill: {
    height: '100%',
    backgroundColor: tokens.colorBrandForeground1,
    borderRadius: tokens.borderRadiusSmall,
    transitionProperty: 'width',
    transitionDuration: tokens.durationSlower,
    transitionTimingFunction: tokens.curveDecelerateMax,
    '@media (prefers-reduced-motion: reduce)': {
      transitionDuration: '0.01ms',
    },
  },
  progressLabel: {
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground3,
    fontVariantNumeric: 'tabular-nums',
    minWidth: '32px',
    textAlign: 'right' as const,
  },

  // Steps list
  stepsList: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXXS,
  },
  // Exit animation
  containerExiting: {
    animationName: {
      from: { opacity: 1, transform: 'translateY(0)' },
      to: { opacity: 0, transform: 'translateY(-8px)' },
    },
    animationDuration: tokens.durationNormal,
    animationTimingFunction: tokens.curveDecelerateMax,
    animationFillMode: 'forwards',
    '@media (prefers-reduced-motion: reduce)': {
      animationDuration: '0.01ms',
    },
  },

  // Celebration state
  celebrationContent: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalM,
    paddingBlock: tokens.spacingVerticalS,
  },
  celebrationIcon: {
    color: tokens.colorPaletteGreenForeground1,
    flexShrink: 0,
  },
  celebrationText: {
    fontSize: tokens.fontSizeBase300,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
  },

  // Visually hidden live region
  srOnly: {
    position: 'absolute' as const,
    width: '1px',
    height: '1px',
    paddingTop: 0,
    paddingRight: 0,
    paddingBottom: 0,
    paddingLeft: 0,
    marginTop: '-1px',
    marginRight: '-1px',
    marginBottom: '-1px',
    marginLeft: '-1px',
    overflow: 'hidden' as const,
    clipPath: 'inset(50%)',
    whiteSpace: 'nowrap' as const,
    borderTopWidth: 0,
    borderRightWidth: 0,
    borderBottomWidth: 0,
    borderLeftWidth: 0,
  },

  // Collapsible body
  body: {
    display: 'grid',
    gridTemplateRows: '1fr',
    transitionProperty: 'grid-template-rows, opacity',
    transitionDuration: tokens.durationGentle,
    transitionTimingFunction: tokens.curveDecelerateMax,
    '@media (prefers-reduced-motion: reduce)': {
      transitionDuration: '0.01ms',
    },
  },
  bodyCollapsed: {
    gridTemplateRows: '0fr',
    opacity: 0,
  },
  bodyInner: {
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
  },
});

export interface OnboardingChecklistProps {
  /** Called when user clicks the "Add application" step */
  onAddApplication?: () => void;
}

export const OnboardingChecklist = ({ onAddApplication }: OnboardingChecklistProps) => {
  const styles = useStyles();
  const { trackEvent } = useTelemetry();
  const { completedSteps, dismissed, minimized, dismissOnboarding, toggleMinimized } = useOnboardingProgress();

  const progressPercent = getProgressPercent(completedSteps);
  const isComplete = completedSteps.length === ONBOARDING_STEPS.length;

  const [exiting, setExiting] = useState(false);
  const [celebrating, setCelebrating] = useState(false);
  const [liveAnnouncement, setLiveAnnouncement] = useState('');
  const prevCompleteRef = useRef(isComplete);
  const hasTrackedView = useRef(false);
  const prevCompletedCountRef = useRef(completedSteps.length);

  // Track checklist view on mount
  useEffect(() => {
    if (!dismissed && !isComplete && !hasTrackedView.current) {
      hasTrackedView.current = true;
      trackEvent('onboarding.checklist.viewed', { stepsCompleted: completedSteps.length });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Announce step completion via live region
  useEffect(() => {
    if (completedSteps.length > prevCompletedCountRef.current) {
      const latestStepId = completedSteps[completedSteps.length - 1];
      const step = ONBOARDING_STEPS.find((s) => s.id === latestStepId);
      if (step) {
        setLiveAnnouncement(
          `Step completed: ${step.title}. ${getProgressPercent(completedSteps)}% complete.`
        );
      }
    }
    prevCompletedCountRef.current = completedSteps.length;
  }, [completedSteps]);

  // Detect completion transition → celebrate → exit
  useEffect(() => {
    if (isComplete && !prevCompleteRef.current && !dismissed) {
      trackEvent('onboarding.checklist.completed');
      setCelebrating(true);
      const timer = setTimeout(() => setExiting(true), 2500);
      return () => clearTimeout(timer);
    }
    prevCompleteRef.current = isComplete;
  }, [isComplete, dismissed, trackEvent]);

  // Already dismissed from localStorage
  if (dismissed) return null;
  // Already complete on mount and not celebrating/exiting
  if (isComplete && !celebrating && !exiting) return null;

  const handleDismiss = () => {
    trackEvent('onboarding.checklist.dismissed', {
      stepsCompletedAtDismiss: completedSteps.length,
      progressPercent,
    });
    setExiting(true);
  };

  const handleToggleMinimized = () => {
    trackEvent('onboarding.checklist.minimized', {
      action: minimized ? 'expand' : 'minimize',
    });
    toggleMinimized();
  };

  const handleAnimationEnd = (e: React.AnimationEvent) => {
    if (exiting && e.currentTarget === e.target) {
      dismissOnboarding();
    }
  };

  return (
    <section
      className={mergeClasses(styles.container, exiting && styles.containerExiting)}
      aria-label="Getting started checklist"
      role="region"
      onAnimationEnd={handleAnimationEnd}
    >
      {/* Live region for screen reader announcements */}
      <div className={styles.srOnly} aria-live="polite" role="status">
        {liveAnnouncement}
      </div>

      {/* Header */}
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <h2 className={styles.heading}>
            {celebrating ? "You're all set!" : 'Get started'}
          </h2>
          <Text className={styles.subtitle}>
            {celebrating
              ? 'Start building amazing workflows'
              : minimized
                ? `${progressPercent}% complete`
                : 'Complete these steps to set up your first workflow'}
          </Text>
        </div>
        {!celebrating && (
          <div className={styles.headerActions}>
            <Tooltip content={minimized ? 'Expand' : 'Minimize'} relationship="label">
              <Button
                className={styles.headerButton}
                appearance="subtle"
                icon={minimized ? <ChevronDown16Regular /> : <ChevronUp16Regular />}
                onClick={handleToggleMinimized}
                aria-label={minimized ? 'Expand onboarding checklist' : 'Minimize onboarding checklist'}
                aria-expanded={!minimized}
                aria-controls="onboarding-body"
                size="small"
              />
            </Tooltip>
            <Tooltip content="Don't show again" relationship="label">
              <Button
                className={styles.headerButton}
                appearance="subtle"
                icon={<Dismiss16Regular />}
                onClick={handleDismiss}
                aria-label="Dismiss onboarding checklist"
                size="small"
              />
            </Tooltip>
          </div>
        )}
      </div>

      {/* Collapsible body */}
      <div
        id="onboarding-body"
        className={mergeClasses(styles.body, minimized && styles.bodyCollapsed)}
        aria-hidden={minimized}
        // @ts-expect-error inert is a valid HTML attribute but not yet in React types
        inert={minimized ? '' : undefined}
      >
        <div className={styles.bodyInner}>
          {celebrating ? (
            <div className={styles.celebrationContent}>
              <CheckmarkCircle24Filled className={styles.celebrationIcon} aria-hidden="true" />
              <Text className={styles.celebrationText}>
                All steps complete — you&apos;re ready to automate!
              </Text>
            </div>
          ) : (
            <>
              {/* Progress bar */}
              <div className={styles.progressRow} role="progressbar" aria-valuenow={progressPercent} aria-valuemin={0} aria-valuemax={100} aria-label={`Onboarding progress: ${progressPercent}%`}>
                <div className={styles.progressTrack}>
                  <div
                    className={styles.progressFill}
                    style={{ width: `${progressPercent}%` }}
                  />
                </div>
                <Text className={styles.progressLabel}>{progressPercent}%</Text>
              </div>

              {/* Steps */}
              <div className={styles.stepsList} role="list" aria-label="Onboarding steps">
                {ONBOARDING_STEPS.map((step, index) => (
                  <OnboardingStep
                    key={step.id}
                    stepId={step.id}
                    title={step.title}
                    description={step.description}
                    route={step.route}
                    status={getStepStatus(step.id, completedSteps)}
                    stepNumber={index + 1}
                    animationDelay={BASE_DELAY_MS + index * STAGGER_DELAY_MS}
                    {...(step.id === 'add-application' && onAddApplication ? { onAction: onAddApplication } : {})}
                  />
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </section>
  );
};
